import React, { useEffect, useState, useRef } from 'react'
import { ProgressBar, FileUploader, notification } from '@edulastic/common'
import { withNamespaces } from 'react-i18next'
import { createPortal } from 'react-dom'
import { isEmpty } from 'lodash'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { withRouter } from 'react-router'
import IconInfo from '@edulastic/icons/src/IconInfoCircle'
import { fileTypes, aws } from '@edulastic/constants'
import {
  views,
  fileUploadStatusTypes,
} from '@edulastic/common/src/components/FileUploader/constants'
import { segmentApi } from '@edulastic/api'
import * as pdfjsLib from 'pdfjs-dist'
import { uploadToS3 } from '@edulastic/common/src/helpers'
import { StyledDocUploadContainer, StyledFooter } from '../components/styled'
import { getCurriculumsListSelector } from '../../../../src/selectors/dictionaries'
import {
  deleteImportedTestAction,
  getImportTestFromDocSelector,
  importTestFromDocAction,
  setImportTestFromDocStateAction,
} from '../ducks'
import { getDictCurriculumsAction } from '../../../../src/actions/dictionaries'
import { initialAlignmentData, testFromDocInitialData } from '../constants'
import { isTestFromDocDataValidAndNotify } from '../utils'
import { getDefaultInterests } from '../../../../dataUtils'
import AfterFooterButton from '../components/AfterFooterButton'
import ImportSummary from '../components/ImportSummary'
import StandardsInput from '../components/StandardsInput'
import PDFWarningModal from '../components/PDFWarningModal'
import appConfig from '../../../../../../app-config'

pdfjsLib.GlobalWorkerOptions.workerSrc = appConfig.getPdfJsDistWorkerUrl(
  pdfjsLib.version
)

/**
 * Get the page images from a pdf file using offscreen canvas
 *
 * @param {*} pdfFile
 */
async function getPdfPageImages(pdfFile) {
  const arrayBuffer = await pdfFile.arrayBuffer()
  const pdfDoc = await pdfjsLib.getDocument(arrayBuffer).promise
  const numPages = pdfDoc.numPages
  const filename = pdfFile.name?.split('.')[0]
  const pageImages = []
  for (let i = 1; i <= numPages; i++) {
    const page = await pdfDoc.getPage(i)
    const viewport = page.getViewport({ scale: 3 })
    const offscreenCanvas = new OffscreenCanvas(viewport.width, viewport.height)
    const context = offscreenCanvas.getContext('2d')
    await page.render({ canvasContext: context, viewport }).promise
    const blob = await offscreenCanvas.convertToBlob({ type: 'image/png' })
    const file = new File([blob], `${filename}-page-${i}.png`, {
      type: 'image/png',
    })
    pageImages.push(file)
  }
  return pageImages
}

const ImportTestFromDoc = ({
  t: i18translate,
  fileDropAreaRef,
  createTestRef,
  footerRef,
  allCurriculums,
  importTestFromDocState,
  getAllCurriculums,
  importTestFromDoc,
  setImportTestFromDocState,
  history,
  deleteImportedTest,
}) => {
  const [isDocUploadInProgress, setIsDocUploadInProgress] = useState(false)
  const [testFromDocData, setTestFromDocData] = useState(testFromDocInitialData)
  const [resetFileUploaderData, setResetFileUploaderData] = useState(false)
  const [pdfWarningModalOpen, setPdfWarningModalOpen] = useState(false)
  const fileUploaderRef = useRef(null)
  const [allPagesUploaded, setAllPagesUploaded] = useState(false)
  const [pageImageUrls, setPageImageUrls] = useState([])
  const [pageUploading, setPageUploading] = useState(false)
  const [pageImagesUploadProgress, setPageImagesUploadProgress] = useState(0)

  const {
    apiStatus: importTestFromDocAPIStatus = '',
    result: importedQuestionsData = {},
  } = importTestFromDocState || {}

  const { metaData = {}, test: { _id } = {} } = importedQuestionsData || {}

  const {
    cdnUrl: uploadedDocCdnUrl,
    fileName = '',
    alignment = {},
  } = testFromDocData

  const updateTestFromDocData = (updateData) => {
    setTestFromDocData((prevData) => ({ ...prevData, ...updateData }))
  }

  const updateAlignmentData = (value) => {
    updateTestFromDocData({ alignment: { ...alignment, ...value } })
  }

  const onUploadInProgress = () => {
    setIsDocUploadInProgress(true)
    setResetFileUploaderData(false)
  }

  const onFileDelete = () => {
    updateTestFromDocData({ cdnUrl: '', fileName: '' })
    setIsDocUploadInProgress(false)
  }

  const onUploadFinish = ({ filesData }) => {
    const { cdnUrl, status, name = '' } = filesData?.[0] || {}
    if (status === fileUploadStatusTypes.SUCCESS) {
      const dotSplittedFileName = name.split('.')
      updateTestFromDocData({
        cdnUrl,
        fileName:
          typeof name === 'string'
            ? dotSplittedFileName.length > 1
              ? dotSplittedFileName.slice(0, -1).join('.')
              : name
            : '',
      })
      return
    }
    updateTestFromDocData({ cdnUrl: '' })
    setIsDocUploadInProgress(false)
  }

  const createTestFromDoc = () => {
    if (!isTestFromDocDataValidAndNotify(testFromDocData)) {
      return
    }
    segmentApi.genericEventTrack('TestImport:Document', {
      action: 'createTest',
    })
    importTestFromDoc({
      ...testFromDocData,
      ...(pageImageUrls.length ? { pageUrls: pageImageUrls } : {}),
    })
  }

  const resetData = () => {
    setImportTestFromDocState({
      apiStatus: false,
      result: {},
      errorMessage: '',
    })
    setIsDocUploadInProgress(false)
    updateTestFromDocData({
      cdnUrl: '',
      fileName: '',
      alignment: { ...alignment, standards: [], domains: [] },
    })
    setPageImageUrls([])
  }

  const processQuestionsAndGoToTestReview = () => {
    segmentApi.genericEventTrack('TestImport:Document', {
      action: 'review',
    })
    // do not remove this block as creates issue with import summary modal
    resetData()
    if (_id) {
      history.push(`/author/tests/tab/review/id/${_id}`)
    }
  }

  const discardAndRestart = () => {
    if (_id) {
      deleteImportedTest({ _id })
    }
    segmentApi.genericEventTrack('TestImport:Document', {
      action: 'discardAndRestart',
    })
    resetData()
    setResetFileUploaderData(true)
  }

  useEffect(() => {
    resetData()
    setResetFileUploaderData(false)
  }, [])

  useEffect(() => {
    if (isEmpty(allCurriculums)) {
      getAllCurriculums()
    }
    const defaultInterests = getDefaultInterests()
    let defaultSubject = defaultInterests?.subject
    defaultSubject =
      (Array.isArray(defaultSubject) ? defaultSubject[0] : defaultSubject) ||
      undefined
    const defaultGrades = defaultInterests?.grades || []
    const defaultCurriculumId = defaultInterests?.curriculumId || null
    let alignmentData = {
      ...initialAlignmentData,
      subject: defaultSubject,
      grades: defaultGrades,
      curriculumId: defaultCurriculumId,
    }

    if (allCurriculums?.length) {
      const defaultCurriculum = allCurriculums.find(
        (curriculum) => curriculum?._id === defaultCurriculumId
      )
      if (!isEmpty(defaultCurriculum)) {
        alignmentData = {
          ...alignmentData,
          curriculumId: defaultCurriculum._id,
          curriculum: defaultCurriculum.curriculum,
        }
      }
    }

    updateTestFromDocData({ alignment: alignmentData })
  }, [allCurriculums])

  const fileUploadErrorHandler = () => {
    notification({
      type: 'warn',
      msg:
        'Unsupported file. Please upload a file in .docx and pdf format, and ensure it is less than 20MB.',
    })
  }

  const fileUploadMaxCountHandle = () => {
    notification({
      type: 'warn',
      msg: i18translate('maxFileErrorMessage'),
    })
  }

  const onFileSelectCallback = async (filesData, rawFilesRef) => {
    setPageImageUrls([])
    const pdfUploaded = filesData.some(
      (fileData) =>
        fileData.type === 'application/pdf' ||
        fileData.mimeType === 'application/pdf'
    )
    setPdfWarningModalOpen(pdfUploaded)
    if (pdfUploaded) {
      if (rawFilesRef.current?.length > 0) {
        setPageUploading(true)
        const pdfFile = rawFilesRef.current?.[0]
        const _pageImages = await getPdfPageImages(pdfFile)
        setAllPagesUploaded(false)
        const totalPages = _pageImages.length
        let uploadedPages = 0
        const _pageImageUrls = await Promise.all(
          _pageImages.map((pageImage) =>
            uploadToS3(
              pageImage,
              aws.s3Folders.DEFAULT,
              () => {},
              null,
              'pdf-page-images-prototype'
            ).finally(() => {
              uploadedPages++
              setPageImagesUploadProgress((uploadedPages / totalPages) * 100)
              if (uploadedPages === totalPages) {
                setAllPagesUploaded(true)
              }
            })
          )
        )
        setPageImageUrls(_pageImageUrls)
        setPageUploading(false)
      }
    } else {
      setAllPagesUploaded(true)
      setPageUploading(false)
    }
  }

  const onPdfWarningModalClose = () => {
    setPdfWarningModalOpen(false)
  }

  const discardAndRestartToUploadDocx = () => {
    if (fileUploaderRef.current) {
      fileUploaderRef.current.resetForDocxUpload()
    }
    setPdfWarningModalOpen(false)
  }

  return (
    <>
      <ImportSummary
        importTestFromDocAPIStatus={importTestFromDocAPIStatus}
        metaData={metaData}
        fileName={fileName}
        processQuestionsAndGoToTestReview={processQuestionsAndGoToTestReview}
        discardAndRestart={discardAndRestart}
      />
      <PDFWarningModal
        isOpen={pdfWarningModalOpen}
        onClose={onPdfWarningModalClose}
        reset={discardAndRestartToUploadDocx}
      />
      <StyledDocUploadContainer>
        <FileUploader
          ref={fileUploaderRef}
          fileDropAreaRef={fileDropAreaRef}
          maxFilesCount={1}
          onFileSelectCallback={onFileSelectCallback}
          allowedFileTypes={[
            fileTypes.PDF,
            fileTypes.DOCX,
            fileTypes.GOOGLE_DOC,
          ]}
          view={views.TEST_SELECT_DOC_UPLOAD}
          s3Folder={aws.s3Folders.DEFAULT}
          maxFileSize={20}
          uploadType="AI_ATTACHMENT"
          resetAllData={resetFileUploaderData}
          fileUploadProgressCallback={onUploadInProgress}
          fileUploadFinishCallback={onUploadFinish}
          fileDeleteCallback={onFileDelete}
          maxFileSizeExceedCallback={fileUploadErrorHandler}
          unsupportedFileCallback={fileUploadErrorHandler}
          maxFileCountExceededCallback={fileUploadMaxCountHandle}
          eventSource="TestImport:Document"
        />
        {(isDocUploadInProgress || !!uploadedDocCdnUrl?.length) && (
          <StandardsInput
            updateAlignmentData={updateAlignmentData}
            testFromDocData={testFromDocData}
            alignment={alignment}
            allCurriculums={allCurriculums}
          />
        )}
      </StyledDocUploadContainer>
      {!isDocUploadInProgress &&
        footerRef?.current &&
        createPortal(
          <StyledFooter>
            <IconInfo width="20" height="20" />
            <p>
              Note : We are supporting MCQ, MSQ, True/False, Short Text Entry,
              and Fill in the Blanks. Passages are supported as context blocks
              linked to questions of these five types.
            </p>
          </StyledFooter>,
          footerRef.current
        )}
      {footerRef?.current &&
        createPortal(
          <>
            {allPagesUploaded ? null : pageImagesUploadProgress > 0 ? (
              <ProgressBar percent={pageImagesUploadProgress.toFixed(1)} />
            ) : null}
            <AfterFooterButton
              pageUploading={pageUploading}
              isDocUploadInProgress={isDocUploadInProgress}
              uploadedDocCdnUrl={allPagesUploaded ? uploadedDocCdnUrl : null}
              createTestFromDoc={createTestFromDoc}
            />
          </>,
          createTestRef.current
        )}
    </>
  )
}

const enhance = compose(
  withRouter,
  withNamespaces('importTest'),
  connect(
    (state) => ({
      allCurriculums: getCurriculumsListSelector(state),
      importTestFromDocState: getImportTestFromDocSelector(state),
    }),
    {
      getAllCurriculums: getDictCurriculumsAction,
      importTestFromDoc: importTestFromDocAction,
      setImportTestFromDocState: setImportTestFromDocStateAction,
      deleteImportedTest: deleteImportedTestAction,
    }
  )
)

export default enhance(ImportTestFromDoc)
